/**
 * Auth Service Adapter
 *
 * Adapts IAuthService (async methods) to AuthService interface (sync property)
 * required by the storage factory.
 */

import type { IAuthService } from '../auth/auth.service.interface'
import type { AuthService } from './factory'
import { reportError } from '@/lib/error-reporting'

/**
 * Adapter that wraps IAuthService to match AuthService interface
 * Caches authentication state and token for synchronous access
 */
export class AuthServiceAdapter implements AuthService {
  private cachedToken: string | null = null
  private cachedIsAuthenticated: boolean = false
  private readonly authService: IAuthService
  private tokenRefreshPromise: Promise<string> | null = null

  constructor(authService: IAuthService) {
    this.authService = authService
    // Initialize cache asynchronously
    this.refreshAuthState()
  }

  /**
   * Refresh cached authentication state
   */
  private async refreshAuthState(): Promise<void> {
    try {
      const authResult = await this.authService.isAuthenticated()
      if (authResult.ok) {
        this.cachedIsAuthenticated = authResult.value

        if (authResult.value) {
          const tokenResult = await this.authService.getAccessToken()
          if (tokenResult.ok) {
            this.cachedToken = tokenResult.value
          }
        }
      }
    } catch (error) {
      reportError(error, { where: 'auth-service-adapter.refreshAuthState' })
      this.cachedIsAuthenticated = false
      this.cachedToken = null
    }
  }

  /**
   * Get access token (async from IAuthService, returns cached or fetches new)
   */
  async getAccessToken(): Promise<string> {
    // If we already have a pending refresh, wait for it
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise
    }

    // If we have a cached token, return it
    if (this.cachedToken) {
      return this.cachedToken
    }

    // Fetch new token — if first attempt fails, try a silent checkSession before giving up
    this.tokenRefreshPromise = (async () => {
      try {
        let result = await this.authService.getAccessToken()

        if (!result.ok) {
          // Attempt silent re-authentication (uses hidden iframe / Auth0 SSO session).
          // If the SSO session is still alive this renews the tokens without interrupting
          // the user. Only re-throw if this also fails.
          const sessionResult = await this.authService.checkSession()
          if (sessionResult.ok) {
            result = await this.authService.getAccessToken()
          }
        }

        if (result.ok) {
          this.cachedToken = result.value
          this.cachedIsAuthenticated = true
          return result.value
        }
        throw new Error('Failed to get access token')
      } finally {
        this.tokenRefreshPromise = null
      }
    })()

    return this.tokenRefreshPromise
  }

  /**
   * Check if user is authenticated (synchronous property)
   */
  get isAuthenticated(): boolean {
    return this.cachedIsAuthenticated
  }

  /**
   * Manually update cached authentication state (call after login/logout)
   */
  async updateAuthState(): Promise<void> {
    await this.refreshAuthState()
  }
}
